import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

// core components
import { loadPartnersList, loadShareholders, saveShareholders, loadPartnerDetails } from '../actionCreators';
import { loadPartnerTypes } from 'features/offer/edit/client/form/actionCreators';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import OfferPartnerShareholderComponent from './OfferPartnerShareholderComponent';
import { emptyObject } from 'constants/emptyConst';
import {
  shape,
  required,
  concatFailure,
  items,
  Validator,
  Success,
  Failure,
  ValidationError,
  unique,
  errorMessage,
  fromModel
} from '@totalsoft/pure-validations';
import { withTranslation } from 'react-i18next';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { isMobilePhoneByCountry } from '../../../../../../utils/pure-validation/primitiveValidators';
import { compose } from 'redux';
import { withParams } from '../../../../../../providers/ParamsConsumer';
import { withRights } from 'providers/RightsConsumer';

const OfferPartnerShareholderContainer = ({
  documentId,
  siteId,
  partnerId,
  t,
  openMasterPartner,
  openDocumentPartner,
  masterPartnerApp,
  clientShareholdersWrite,
  params,
  isAssociatedPartner,
  rights
}) => {
  const [localShareholders, setLocalShareholderList] = useState([]);
  const [shareholderCardExpanded, setShareholderCardExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const realBeneficiaryThreshold = 25;

  const dispatch = useDispatch();

  const shareholders = useSelector(
    state => (state.entities.offerPartnerShareholders ? state.entities.offerPartnerShareholders[partnerId] : []),
    shallowEqual
  );

  const partnerTypes = useSelector(state => state.clientData.partnerTypes, shallowEqual);
  const shareholdersLoaded = useSelector(
    state => (state.currentOffer.clientShareholdersData[partnerId] ? state.currentOffer.clientShareholdersData[partnerId].loaded : false),
    shallowEqual
  );

  const totalPercentageValidator = Validator(function totalPercentageValidator(content) {
    return shareholdersLoaded && istotalPercentageValid(content)(100)
      ? Success
      : Failure(ValidationError(t('OfferEdit.Client.Shareholder.ValidateTotalPercent')));
  });

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const validatorShareholderValidator =
    [
      unique(x => x.uniqueID || emptyObject) |> errorMessage('OfferEdit.Client.Shareholder.ValidateUniqueId'),
      items(
        fromModel(_item =>
          shape({
            uniqueID: required,
            sharePercent: required,
            shareHolderPartnerName: required,
            shareHolderPhoneNumber: isMobilePhoneByCountry(params.countryCode),
            partnerTypeId: required
          })
        )
      ),
      totalPercentageValidator
    ] |> concatFailure;

  const [validationShareholderSelection, validateShareholderSelection, resetDirtyInfo] = useDirtyFieldValidation(
    validatorShareholderValidator,
    dirtyInfo
  );

  const localSaveShareholders = (partnerId, localShareholders) => {
    if (!validateShareholderSelection(localShareholders)) {
      return;
    }

    setSaving(true);
    dispatch(saveShareholders(documentId, siteId, partnerId, localShareholders)).then(() => {
      setSaving(false);
      resetDirtyInfo();
    });
    setSaveDisabled(true);
  };

  const emptyShareholder = {
    isRealBeneficiary: false,
    partnerId: partnerId,
    partnerShareHolderId: 0,
    shareHolderPartnerId: null,
    shareHolderPartnerName: '',
    shareHolderPhoneNumber: '',
    sharePercent: null,
    shareholders: [],
    siteId: siteId,
    uniqueID: '',
    partnerTypeId: null
  };

  const addShareholder = () => {
    const shareholderList = [...localShareholders, emptyShareholder];
    setLocalShareholderList(shareholderList);
    setSaveDisabled(false);
  };

  const deleteShareholder = item => {
    setDirtyInfo(`${localShareholders.indexOf(item)}`);
    const newShareholderList = localShareholders.filter(l => localShareholders.indexOf(l) !== localShareholders.indexOf(item));
    setLocalShareholderList(newShareholderList);
    setSaveDisabled(false);
  };

  const onShareholderCardExpanded = (_, expanded) => {
    setShareholderCardExpanded(!expanded);
  };

  useEffect(() => {
    if (shareholderCardExpanded && !shareholdersLoaded && partnerId !== null) {
      dispatch(loadPartnerTypes());
      dispatch(loadShareholders(partnerId, documentId, siteId));
    }
  }, [shareholderCardExpanded, documentId, partnerId, dispatch, siteId, shareholdersLoaded]);

  useEffect(() => {
    if (shareholdersLoaded) setLocalShareholderList(shareholders);
    else if (shareholderCardExpanded && partnerId !== null) dispatch(loadShareholders(partnerId, documentId, siteId));
  }, [shareholdersLoaded, shareholderCardExpanded, partnerId, documentId, siteId, dispatch, shareholders]);

  useEffect(() => {
    validateShareholderSelection(localShareholders, dirtyInfo);
  }, [localShareholders, dirtyInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPartners = search => {
    if (!search) return Promise.resolve([]);
    return dispatch(loadPartnersList(search)).then(data => {
      return data.map(item => {
        return { id: item.id, uniqueID: item.uniqueId };
      });
    });
  };

  const localLoadPartnerDetails = (uniqueID, idx) => {
    if (!uniqueID) {
      return Promise.resolve({ options: [] });
    }
    dispatch(loadPartnerDetails(uniqueID)).then(data => {
      const list = Object.assign([], {
        ...localShareholders,
        [idx]: {
          ...localShareholders[idx],
          uniqueID: data?.uniqueID ? data.uniqueID : uniqueID,
          partnerTypeId: data?.parentPartnerType?.partnerTypeId
            ? data.parentPartnerType.partnerTypeId
            : localShareholders[idx].partnerTypeId,
          shareHolderPartnerName: data?.partnerName ? data.partnerName : localShareholders[idx].personName,
          isPF: data?.parentPartnerType?.isPF,
          sharePercent: null,
          isRealBeneficiary: false,
          shareHolderPhoneNumber: data?.phoneNumber ? data.phoneNumber : localShareholders[idx].phone,
          shareholderPartnerId: data?.partnerId && data?.partnerId !== 0 ? data.partnerId : null,
          shareholders: data?.shareholders,
          partnerExists: data?.partnerExists ? data.partnerExists : false
        }
      });
      setLocalShareholderList(list);
    });
  };

  const onClearShareholder = idx => {
    setDirtyInfo(`${idx}`);
    let newShareholders = localShareholders;
    let shareholdersList = Object.assign([], {
      ...newShareholders,
      [idx]: {
        ...newShareholders[idx],
        uniqueID: null,
        shareHolderPartnerName: null,
        email: null,
        shareHolderPhoneNumber: null,
        shareholderPartnerId: null,
        isRealBeneficiary: false,
        sharePercent: null,
        partnerTypeId: null,
        shareholders: []
      }
    });
    setLocalShareholderList(shareholdersList);
    setSaveDisabled(false);
  };

  const onShareholderPropertyChange =
    idx =>
    (prop1, prop2, callbackFn = () => {}) =>
    (value1, value2) => {
      setDirtyInfo(`${idx}.${prop1}`);
      const isSharePercentProp = prop1 === 'sharePercent';
      const exceedThreshold = value1 >= realBeneficiaryThreshold;

      if (prop1 === 'partnerTypeId') {
        setLocalShareholderList(
          Object.assign([], {
            ...localShareholders,
            [idx]: {
              ...localShareholders[idx],
              [prop1]: value1
            }
          })
        );
      } else {
        setLocalShareholderList(
          Object.assign([], {
            ...localShareholders,
            [idx]: {
              ...localShareholders[idx],
              [prop1]: value1,
              ...(prop2 && { [prop2]: value2 }),
              ...(isSharePercentProp && { isRealBeneficiary: exceedThreshold && localShareholders[idx].isPF })
            }
          })
        );
      }
      if (value2) {
        callbackFn(value2, idx);
      }
      setSaveDisabled(false);
    };

  const istotalPercentageValid = localShareholders => requiredPercentage => {
    if (localShareholders === null) return true;
    if (localShareholders.length === 0) return true;

    const totalPercentage = localShareholders.reduce((totalPercentage, shareholder) => {
      return totalPercentage + Number(shareholder.sharePercent === undefined ? 0 : shareholder.sharePercent);
    }, 0);
    if (totalPercentage === requiredPercentage) return true;
    return false;
  };

  const disabled = useMemo(
    () => saving || rights.readOnlyFull || !clientShareholdersWrite,
    [saving, rights.readOnlyFull, clientShareholdersWrite]
  );

  return (
    <OfferPartnerShareholderComponent
      disabled={disabled}
      saving={saving}
      shareholderList={localShareholders}
      partnerTypes={partnerTypes}
      loadPartners={loadPartners}
      onPropertyChange={onShareholderPropertyChange}
      onClearShareholder={onClearShareholder}
      saveShareholders={localSaveShareholders}
      addShareholder={addShareholder}
      deleteShareholder={deleteShareholder}
      partnerId={partnerId}
      documentId={documentId}
      siteId={siteId}
      shareholderCardExpanded={shareholderCardExpanded}
      onShareholderCardExpanded={onShareholderCardExpanded}
      loadPartnerDetails={localLoadPartnerDetails}
      saveDisabled={saveDisabled}
      validationShareholderSelection={validationShareholderSelection}
      totalPercentageValidator={totalPercentageValidator}
      shareholdersLoaded={shareholdersLoaded}
      openMasterPartner={openMasterPartner}
      openDocumentPartner={openDocumentPartner}
      masterPartnerApp={masterPartnerApp}
      clientShareholdersWrite={clientShareholdersWrite}
      isAssociatedPartner={isAssociatedPartner}
    />
  );
};
OfferPartnerShareholderContainer.propTypes = {
  partnerId: PropTypes.number,
  shareholders: PropTypes.array,
  shareholdersLoaded: PropTypes.bool,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  t: PropTypes.func,
  openMasterPartner: PropTypes.func,
  openDocumentPartner: PropTypes.func,
  masterPartnerApp: PropTypes.bool,
  clientShareholdersWrite: PropTypes.bool.isRequired,
  params: PropTypes.object,
  rights: PropTypes.object.isRequired,
  isAssociatedPartner: PropTypes.bool.isRequired
};

export default compose(withTranslation())(withParams(withRights(OfferPartnerShareholderContainer)));
